import {createStyles} from '@mantine/core';
import React, {ReactNode, useEffect, useState} from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import ProcedureRunDesignFlowchart from 'studies/typed/shared/ProcedureRunDesignFlowchart';
import {ConditionGroup} from 'studies/typed/shared/useExperimentDesignState';
import getTestProps from 'test/lib/cypress/getTestProps';
import FlowchartContextPanel, {
  PanelSide,
  SidepanelSize,
} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';
import FlowchartSidebar from 'ui-library/reactflow/sidepanels/FlowchartSidebar';
import {useResizableSidepanel} from 'ui-library/reactflow/sidepanels/useResizableSidepanel';

import {FlowchartEdgeSpec, StatusByNodeId} from './ProcedureRunDesignFlowchartLayoutUtils';
import ProcedureRunMethodSelectDropdown from './ProcedureRunMethodSelectDropdown';

const LEFT_PANEL_SIZE = SidepanelSize.SMALL;
const RIGHT_PANEL_SIZE = SidepanelSize.MEDIUM;

interface ProcedureRunDesignFlowchartEditorProps {
  conditions: Array<ConditionGroup>;
  methods: Array<ExperimentMethod>;
  activeMethodId: string | null;
  procedureType: ProcedureType;
  selectedMethodId: string | null;
  onChangeSelectedMethod: (methodId: string | null) => void;
  isInEditState: boolean;
  /**
   * When false (editing an existing procedure run), persisted conditions render a lock icon in
   * their flowchart nodes.
   */
  allowEditingPersistedConditions: boolean;
  leftPanelChildren: ReactNode;
  rightPanelChildren: ReactNode;
  statusByNodeId?: StatusByNodeId;
  edgeSpecs: Array<FlowchartEdgeSpec>;
}

export default function ProcedureRunDesignFlowchartEditor(
  props: ProcedureRunDesignFlowchartEditorProps
): JSX.Element {
  const {classes, cx} = useStyles();
  const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(false);
  const [selectedLeftSidebarIconIndex, setSelectedLeftSidebarIconIndex] = useState<number | null>(null);
  const {
    width: resizedWidth,
    isResizing,
    handleMouseDown: handleResizeMouseDown,
    containerRef,
    onPanelSizeChange,
  } = useResizableSidepanel({
    storageKey: 'studies-procedure-design-right-sidepanel-width',
    defaultPanelSize: RIGHT_PANEL_SIZE,
  });

  // Open left sidepanel when user enters edit mode
  useEffect(() => {
    if (!props.isInEditState) {
      return;
    }

    setIsLeftPanelOpen(true);
    setSelectedLeftSidebarIconIndex(0);
  }, [props.isInEditState]);

  return (
    <div
      ref={containerRef}
      className={classes.root}
      {...getTestProps({component: 'ProcedureRunDesignFlowchartEditor', element: 'root'})}
    >
      <FlowchartSidebar
        icons={[
          {
            name: 'create',
            onClick: () => {
              setIsLeftPanelOpen(true);
            },
          },
        ]}
        selectedIconIndex={selectedLeftSidebarIconIndex}
        setSelectedIconIndex={setSelectedLeftSidebarIconIndex}
      />
      <FlowchartContextPanel
        panelSide={PanelSide.LEFT}
        sidepanelName="Run Plan"
        isOpen={isLeftPanelOpen}
        onClose={() => {
          setIsLeftPanelOpen(false);
          setSelectedLeftSidebarIconIndex(null);
        }}
        size={LEFT_PANEL_SIZE}
        showExpandCollapseButtons={false}
      >
        {props.leftPanelChildren}
      </FlowchartContextPanel>
      <div className={cx(classes.flowchartContainer)}>
        <ProcedureRunDesignFlowchart
          conditions={props.conditions}
          methods={props.methods}
          activeMethodId={props.activeMethodId}
          procedureType={props.procedureType}
          controlsShiftLeft={0}
          statusByNodeId={props.statusByNodeId}
          selectedMethodId={props.selectedMethodId}
          setSelectedMethodId={props.onChangeSelectedMethod}
          edgeSpecs={props.edgeSpecs}
          allowEditingPersistedConditions={props.allowEditingPersistedConditions}
          isEditable={props.isInEditState}
        />
      </div>
      <FlowchartContextPanel
        panelSide={PanelSide.RIGHT}
        sidepanelName="Design"
        headerContent={
          props.selectedMethodId != null ? (
            <ProcedureRunMethodSelectDropdown
              methods={props.methods}
              selectedMethodId={props.selectedMethodId}
              onChangeSelectedMethod={props.onChangeSelectedMethod}
            />
          ) : undefined
        }
        // The right ("Design") panel is open exactly when a method is selected. Closing it clears the
        // selection (matching the Workflows flowchart editor); selecting a column reopens it.
        isOpen={props.selectedMethodId != null}
        onClose={() => {
          props.onChangeSelectedMethod(null);
        }}
        size={RIGHT_PANEL_SIZE}
        showExpandCollapseButtons={true}
        resizable={true}
        resizedWidth={resizedWidth}
        isResizing={isResizing}
        onResizeMouseDown={handleResizeMouseDown}
        onPanelSizeChange={onPanelSizeChange}
      >
        {props.rightPanelChildren}
      </FlowchartContextPanel>
    </div>
  );
}

const useStyles = createStyles(() => ({
  root: {
    display: 'flex',
    position: 'absolute',
    height: '100%',
    width: '100%',
  },
  flowchartContainer: {
    flex: 1,
    position: 'relative',
    minWidth: 0,
  },
}));
